import {
    Dialog,
    Typography,
    TextField,
    styled,
    Button,
    Grid,
    Alert,
} from '@mui/material';
import { useState, MouseEvent, useEffect } from 'react';
import { Add, Cancel } from '@mui/icons-material';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useRecoilValue } from 'recoil';
import { QUERY_KEY } from 'src/queries/queryForcedRankingStrategies';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createForcedRankingStrategy } from 'src/mutations/createForcedRankingStrategy';
import { generatePath, useNavigate } from 'react-router-dom';
import { validateStrategyName } from 'src/components/ForcedRankingDetails/utils';
import { FORCED_RANKING_DETAILS } from 'src/lib/routes';
import { getUserEmailQuery } from 'src/selectors/getUserEmailQuery';
import { SaveLoadingButton } from '../buttons/SaveLoadingButton';
import { CSSGrid } from '../CSSGrid';

export interface PropTypes {
    open: boolean;
    onClose: () => void;
}

const StyledDialog = styled(Dialog)(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    '& .MuiPaper-root': {
        width: '100%',
        maxWidth: '600px',
    },
    '& .MuiDialog-container': {
        width: '100%',
    },
}));

const DialogContainer = styled(CSSGrid)(({ theme }) => ({
    gap: theme.spacing(4),
    overflow: 'hidden',
    padding: theme.spacing(8),
}));

const ActionButtons = styled(CSSGrid)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(4),
    justifyContent: 'flex-end',
    gridAutoFlow: 'column dense',
    marginTop: theme.spacing(4),
}));

export const CreateNewStrategyModal = ({
    open,
    onClose,
}: PropTypes): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const queryClient = useQueryClient();
    const [productKey, setProductKey] = useState('');
    const [rankingStrategyName, setRankingStrategyName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [nameValidationError, setNameValidationError] = useState<string | null>(null);
    const navigate = useNavigate();
    const userEmail = useRecoilValue(getUserEmailQuery);

    useEffect(() => {
        if (open) {
            setProductKey('');
            setRankingStrategyName('');
            setError(null);
            setNameValidationError(null);
        }
    }, [open]);

    const handleProductKeyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setProductKey(event.target.value.toUpperCase());
        setError(null);
    };

    const handleRankingStrategyNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.target.value;

        setRankingStrategyName(newValue);
        setError(null);

        // Validate the name
        const validationError = validateStrategyName(newValue);

        setNameValidationError(validationError);
    };

    const handleClose = (): void => {
        setProductKey('');
        setRankingStrategyName('');
        onClose();
    };

    const createForcedRankingStrategyMutation = useMutation<
    Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy,
    Error,
    App.ForcedRankings.CreateMutationVariables
    >(
        createForcedRankingStrategy,
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries([QUERY_KEY]);

                navigate(generatePath(FORCED_RANKING_DETAILS, {
                    strategyName: data.name,
                }));

                handleClose();
            },
        },
    );

    const handleNewForcedRankingStrategy = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();
        try {
            if (accessToken && rankingStrategyName && productKey) {
                await createForcedRankingStrategyMutation.mutateAsync(
                    {
                        accessToken,
                        strategyName: rankingStrategyName,
                        body: {
                            referenceProduct: productKey,
                            name: rankingStrategyName,
                            modifiedBy: userEmail,
                        },
                    },
                );
            }
        } catch (err) {
            setError((err as Error).message);
        }
    };

    const isCreating = createForcedRankingStrategyMutation.isLoading;

    return (
        <StyledDialog open={open} onClose={handleClose}>
            <DialogContainer>
                <Typography variant="h6">
                    Create New Forced Ranking Strategy
                </Typography>
                <Typography variant="caption">
                    Create a new forced ranking strategy by providing a name and reference PRD.
                    You will be navigated to the strategy details page after creation.
                </Typography>
                <Grid container gap={4}>
                    <TextField
                        fullWidth
                        required
                        error={!!nameValidationError}
                        helperText={nameValidationError}
                        id="ranking-strategy-name"
                        label="Ranking Strategy Name"
                        size="small"
                        value={rankingStrategyName}
                        onChange={handleRankingStrategyNameChange}
                    />
                    <TextField
                        fullWidth
                        required
                        helperText="Required for the available product options and consolidation settings"
                        id="reference-prd"
                        label="Reference PRD"
                        size="small"
                        value={productKey}
                        onChange={handleProductKeyChange}
                    />
                </Grid>
                {error && <Alert severity="error">{error}</Alert>}
                <ActionButtons>
                    <Button aria-label="cancel" color="secondary" startIcon={<Cancel />} variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <SaveLoadingButton
                        aria-label="create new forced ranking strategy"
                        disabled={!(rankingStrategyName && productKey) || !!nameValidationError || isCreating}
                        Icon={Add}
                        variant="contained"
                        onClick={handleNewForcedRankingStrategy}
                    >
                        Create
                    </SaveLoadingButton>
                </ActionButtons>
            </DialogContainer>
        </StyledDialog>
    );
};
